<template>
  <section class="quote-panel" aria-label="Orçamento por arquivo">
    <h3>Orçamento por arquivo</h3>
    <p>Selecione o cliente no B2B, envie a lista e confira os códigos e as quantidades antes de salvar.</p>
    <label class="quote-upload">PDF, planilha, texto ou imagem (até 5 MB e 100 produtos)
      <input type="file" accept=".pdf,.xlsx,.xls,.csv,.tsv,.txt,.png,.jpg,.jpeg,.webp" :disabled="busy" @change="upload($event)">
    </label>
    <p class="quote-note">O arquivo será enviado ao serviço de IA para leitura. Os preços vêm da tabela do cliente no B2B.</p>
    <p v-if="busy" role="status">{{ progress }}</p>
    <p v-if="error" class="quote-error" role="alert">{{ error }}</p>
    <div v-if="quote">
      <h4>{{ quote.id ? 'Orçamento #' + quote.id : 'Conferência dos produtos' }}</h4>
      <p>{{ quote.cliente }} <span v-if="quote.criadoEm">— {{ date(quote.criadoEm) }}</span></p>
      <p v-if="quote.id">Valores registrados na data do orçamento.</p>
      <p v-if="dirty" role="status">Há alterações. Confira os produtos novamente para atualizar os valores.</p>
      <ol class="quote-items">
        <li v-for="(item, index) in quote.itens" :key="index">
          <p v-if="item.descricaoOriginal">Solicitado: {{ item.descricaoOriginal }}</p>
          <div class="quote-fields">
            <label>Código recebido<input :value="item.codigo" maxlength="120" :disabled="busy || !!quote.id" @input="change(item, 'codigo', $event.target.value)"></label>
            <label>Quantidade<input type="number" min="0.000001" max="1000000" step="any" :value="item.quantidade" :disabled="busy || !!quote.id" @input="change(item, 'quantidade', $event.target.value)"></label>
          </div>
          <p v-if="item.sku">SKU {{ item.sku }} — {{ item.descricao }} ({{ item.unidade }})</p>
          <p v-if="item.precoUnitario != null">Unitário: {{ money(item.precoUnitario) }}<span v-if="item.total != null"> · Total: {{ money(item.total) }}</span></p>
          <p :class="{ 'quote-error': item.status !== 'Encontrado' }">{{ item.status }}</p>
          <button v-if="!quote.id" type="button" :disabled="busy" @click="remove(index)">Remover linha</button>
        </li>
      </ol>
      <p><strong>Total {{ dirty ? '(antes das alterações)' : 'dos itens calculados' }}: {{ money(quote.total) }}</strong></p>
      <p>{{ quote.pendencias }} pendência(s). Itens pendentes não compõem o total.</p>
      <p class="quote-note">{{ quote.observacao }}</p>
      <div class="quote-actions">
        <template v-if="!quote.id">
          <button type="button" :disabled="busy || !quote.itens.length" @click="check">Conferir produtos</button>
          <button type="button" :disabled="busy || dirty || !quote.itens.length" @click="save">Salvar orçamento</button>
        </template>
        <template v-else>
          <button type="button" :disabled="busy" @click="download(quote)">Baixar planilha XLSX</button>
          <button type="button" :disabled="busy" @click="reuse">Usar como base</button>
          <button v-if="!quote.conversao" type="button" :disabled="busy || !matchedCount" @click="convert(false)">Criar pedido com os matches ({{ matchedCount }})</button>
        </template>
      </div>
      <div v-if="quote.conversao" class="quote-conversion" role="status">
        <h4>Pedido {{ quote.conversao.numero || '#' + quote.conversao.pedidoId }} {{ quote.conversao.emEdicao ? 'em edição' : 'já convertido' }}</h4>
        <p>{{ (quote.conversao.incluidos || []).length }} produto(s) incluído(s). Total atual: {{ money(quote.conversao.totalAtual) }}.</p>
        <p>{{ quote.conversao.aviso }}</p>
        <p v-if="quote.conversao.excluidos && quote.conversao.excluidos.length">Itens não incluídos:</p>
        <ul><li v-for="(excluded, index) in quote.conversao.excluidos" :key="index">{{ excluded.codigo || 'Sem código' }} — {{ excluded.motivo }}</li></ul>
        <button type="button" :disabled="busy" @click="convert(true)">{{ quote.conversao.emEdicao ? 'Abrir pedido para conferir e finalizar' : 'Consultar pedido no B2B' }}</button>
      </div>
    </div>
    <div class="quote-history">
      <h4>Meus orçamentos deste cliente</h4>
      <button type="button" :disabled="busy" @click="history">Atualizar lista</button>
      <p v-if="loaded && !quotes.length">Nenhum orçamento salvo.</p>
      <ul><li v-for="saved in quotes" :key="saved.id"><button type="button" :disabled="busy" @click="select(saved)">#{{ saved.id }} · {{ date(saved.criadoEm) }} · {{ money(saved.total) }} · {{ saved.pendencias }} pendência(s)</button></li></ul>
      <p class="quote-note">Últimos 20 orçamentos salvos por você para o cliente selecionado. A conversão cria um pedido em edição somente com os matches válidos. Finalize o envio ao ERP pelo B2B.</p>
    </div>
  </section>
</template>

<script>
export default {
  data() { return { quote: null, quotes: [], busy: false, progress: '', error: null, active: true, dirty: false, loaded: false, requestKey: null }; },
  mounted() { this.history(); },
  beforeDestroy() { this.active = false; },
  computed: {
    matchedCount() { return this.quote ? this.quote.itens.filter(item => item.status === 'Encontrado' && item.sku && Number(item.quantidade) > 0).length : 0; }
  },
  methods: {
    money(value) { return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); },
    date(value) { return value ? new Date(value).toLocaleString('pt-BR') : ''; },
    uuid() {
      const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
      const hex = Array.from(bytes, b => ('0' + b.toString(16)).slice(-2)).join('');
      return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
    },
    payload() {
      return { requisicao: this.requestKey, itens: this.quote.itens.map(item => ({ codigo: item.codigo, descricaoOriginal: item.descricaoOriginal,
        quantidade: item.quantidade === '' || item.quantidade == null ? null : Number(item.quantidade) })) };
    },
    change(item, field, value) {
      if (this.busy || this.quote.id) return;
      item[field] = value; this.dirty = true; this.requestKey = this.uuid();
    },
    remove(index) {
      if (this.busy || this.quote.id) return;
      this.quote.itens.splice(index, 1); this.dirty = true; this.requestKey = this.uuid();
    },
    run(progress, action, success) {
      if (this.busy) return;
      this.busy = true; this.error = null; this.progress = progress;
      return Promise.resolve().then(action).then(response => { if (this.active) success(response); }).catch(response => {
        if (!this.active) return;
        this.error = response.body && response.body.error ? response.body.error : 'Não foi possível concluir. Confira a sessão e o cliente selecionado e tente novamente.';
      }).then(() => { if (this.active) this.busy = false; });
    },
    upload(event) {
      const file = event.target.files && event.target.files[0]; event.target.value = '';
      if (this.busy || !file) return;
      if (file.size <= 0 || file.size > 5 * 1024 * 1024) { this.error = 'Envie um arquivo de até 5 MB.'; return; }
      const form = new FormData(); form.append('arquivo', file);
      return this.run('Lendo o arquivo e consultando produtos…', () => this.$http.post('chat/orcamentos/arquivo', form, { timeout: 150000 }), response => {
        this.quote = response.body; this.dirty = false; this.requestKey = this.uuid();
      });
    },
    check() {
      if (!this.quote || this.quote.id || !this.quote.itens.length) return;
      return this.run('Conferindo produtos e preços…', () => this.$http.post('chat/orcamentos/conferir', this.payload(), { timeout: 150000 }), response => {
        this.quote = response.body; this.dirty = false;
      });
    },
    save() {
      if (!this.quote || this.quote.id || this.dirty || !this.quote.itens.length) return;
      return this.run('Salvando orçamento…', () => this.$http.post('chat/orcamentos', this.payload(), { timeout: 150000 }), response => {
        this.quote = response.body; this.quotes = [response.body].concat(this.quotes.filter(q => q.id !== response.body.id)).slice(0, 20);
      });
    },
    history() {
      return this.run('Buscando orçamentos…', () => this.$http.get('chat/orcamentos'), response => { this.quotes = response.body.orcamentos || []; this.loaded = true; });
    },
    select(quote) { if (!this.busy) { this.quote = JSON.parse(JSON.stringify(quote)); this.dirty = false; this.error = null; } },
    reuse() {
      if (this.busy || !this.quote) return;
      this.quote = Object.assign({}, this.quote, { id: null, criadoEm: null, conversao: null }); this.requestKey = this.uuid(); this.dirty = true;
    },
    convert(open) {
      if (!this.quote || !Number.isSafeInteger(this.quote.id) || this.quote.id <= 0) return;
      const id = this.quote.id;
      return this.run('Preparando pedido com os matches válidos…', () => this.$http.post('chat/orcamentos/' + id + '/pedido', {}, { timeout: 150000 }), response => {
        this.quote = Object.assign({}, this.quote, { conversao: response.body });
        this.quotes = this.quotes.map(saved => saved.id === id ? this.quote : saved);
        if (open) {
          // Reload the B2B so the current order pointer and cart counters come from the authenticated server session.
          window.location.hash = response.body.emEdicao ? '/unit039' : '/unit006';
          window.location.reload();
        }
      });
    },
    download(quote) {
      if (!Number.isSafeInteger(quote.id) || quote.id <= 0) return;
      return this.run('Gerando planilha…', () => this.$http.get('chat/orcamentos/' + quote.id + '/planilha', { responseType: 'blob', timeout: 60000 }), response => {
        const mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        if (!(response.headers.get('Content-Type') || '').toLowerCase().startsWith(mime)) throw new Error('Invalid spreadsheet response');
        const url = URL.createObjectURL(response.body); const link = window.document.createElement('a');
        link.href = url; link.download = 'orcamento_' + quote.id + '.xlsx';
        window.document.body.appendChild(link); link.click(); window.document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      });
    }
  }
};
</script>

<style scoped>
.quote-panel h3 { font-size: 18px; margin: 6px 0 12px; }
.quote-panel h4 { font-size: 16px; margin: 14px 0 8px; }
.quote-panel p, .quote-panel label { font-size: 13px; color: #37474f; }
.quote-upload { display: block; padding: 10px; border: 1px dashed #78909c; border-radius: 6px; }
.quote-upload input { display: block; max-width: 100%; margin-top: 8px; }
.quote-panel .quote-note { font-size: 12px; color: #546e7a; }
.quote-panel .quote-error { color: #b71c1c; }
.quote-items { padding-left: 22px; }
.quote-items li { border-bottom: 1px solid #cfd8dc; padding: 8px 0; }
.quote-fields { display: flex; gap: 12px; }
.quote-fields label { flex: 1; min-width: 0; }
.quote-fields input { width: 100%; box-sizing: border-box; height: 34px; border: 1px solid #90a4ae; border-radius: 4px; padding: 5px; }
.quote-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.quote-panel button { background: #f5f7f8; border: 1px solid #90a4ae; border-radius: 4px; padding: 8px; cursor: pointer; color: #263238; }
.quote-panel button:disabled { opacity: .5; cursor: default; }
.quote-history { border-top: 1px solid #cfd8dc; margin-top: 18px; }
.quote-conversion { border: 1px solid #90a4ae; border-radius: 6px; padding: 10px; margin-top: 12px; background: #f1f7f3; }
.quote-history li { margin-top: 8px; }
.quote-panel button:focus-visible, .quote-panel input:focus-visible { outline: 2px solid #1565c0; outline-offset: 2px; }
</style>



// WEBPACK FOOTER //
// ChatQuotePanel.vue?25b030b3